import type { Types } from '@amplitude/analytics-browser'

const UNMASK_ATTRIBUTE = 'data-amp-unmask'

const ELEMENT_TEXT_PROPERTY = '[Amplitude] Element Text'
const ELEMENT_HIERARCHY_PROPERTY = '[Amplitude] Element Hierarchy'

/** Content of the clicked element. Kept only when it's unmasked. */
const VISIBLE_PROPERTIES = [ELEMENT_TEXT_PROPERTY, '[Amplitude] Element Href']

/**
 * Always dropped, even when unmasked. Aria-labels often hold data the visible text doesn't (e.g. "Remove Jo Bloggs"),
 * and the parent label can come from outside the unmasked element.
 */
const HIDDEN_PROPERTIES = ['[Amplitude] Element Aria Label', '[Amplitude] Element Parent Label']

/** Kept along with `data-*` attributes. They describe structure, not content. */
const STRUCTURAL_ATTRIBUTES = new Set(['role', 'type'])
const MASK_TOKEN = '*****'

interface HierarchyNode {
  attrs?: Record<string, string>
}

/**
 * Session replay hides all text with `defaultMaskLevel: 'conservative'`, but autocapture has no such setting. This
 * plugin does the same for events: it drops element text unless the element sits inside `ftrAmplitudeUnmask`, and
 * strips attributes other than `data-*`, role and type, since ones like aria-label and title can hold data.
 */
export const maskingPlugin: Types.EnrichmentPlugin = {
  name: 'ftr-masking',
  type: 'enrichment',
  execute: async event => {
    const properties = event.event_properties as Record<string, unknown> | undefined
    if (!properties) {
      return event
    }

    // With no hierarchy there's no telling where the element sits, so it counts as masked.
    const hierarchy = (properties[ELEMENT_HIERARCHY_PROPERTY] as HierarchyNode[] | undefined) ?? []
    const isInsideUnmask = hierarchy.some(node => node.attrs && UNMASK_ATTRIBUTE in node.attrs)

    HIDDEN_PROPERTIES.forEach(property => delete properties[property])
    if (isInsideUnmask) {
      removeSvgUndefined(properties)
    } else {
      VISIBLE_PROPERTIES.forEach(property => delete properties[property])
    }
    hierarchy.forEach(node => {
      if (node.attrs) {
        node.attrs = keepStructuralAttributes(node.attrs)
      }
    })

    return event
  },
}

/**
 * Next to masked text, Amplitude reads each child's innerText. SVGs have none, so icons add "undefined"
 * (plugin-autocapture-browser 1.29.3 to 1.30.2). Drop this once it's fixed upstream.
 */
function removeSvgUndefined(properties: Record<string, unknown>): void {
  const text = properties[ELEMENT_TEXT_PROPERTY]
  if (typeof text === 'string' && text.includes(MASK_TOKEN)) {
    properties[ELEMENT_TEXT_PROPERTY] = text.replace(/undefined/g, '')
  }
}

function keepStructuralAttributes(attrs: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(attrs).filter(([name]) => name.startsWith('data-') || STRUCTURAL_ATTRIBUTES.has(name)),
  )
}
